/**
 * SearchUf.jsx
 */

import React, { useCallback, useEffect, useMemo } from 'react';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import { getNoticiasUf } from 'govbr.frontend.policy/actions/search_uf/search_uf';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import DateSocialShareBody from '../Blocks/DateShare/DateSocialShareBody';
import { getNameStateByCode } from '../Blocks/LinkMap/states';
import SpecificTemplate from '../BlockTemplates/Listing/Specific';
import GovBrPagination from '../ProfileReport/GovBrPagination';

const messages = defineMessages({
  title: {
    id: 'News About',
    defaultMessage: 'News About',
  },
  loadingAria: {
    id: 'loading.news.aria',
    defaultMessage: 'Loading news',
  },
  loadingMessage: {
    id: 'loading.news.message',
    defaultMessage: 'Loading news, please wait...',
  },
  noResults: {
    id: 'No results found',
    defaultMessage: 'No results found',
  },
});

const SearchUf = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const { items, loading, total } = useSelector((state) => state.search_uf);

  // -----------------------------
  // URL / Query
  // -----------------------------
  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const page = Number(params.get('page')) || 1;
  const uf_noticia = params.get('uf_noticia');
  const local_context = params.get('local_context')?.toLowerCase() === 'true';

  const baseUrl = 'https://www.gov.br';
  const currentUrl = `${baseUrl}${location.pathname}${location.search}`;

  // -----------------------------
  // Fetch reagindo à URL
  // -----------------------------
  useEffect(() => {
    if (!uf_noticia) {
      history.replace(`/${intl.locale.toLowerCase()}`);
      return;
    }
    dispatch(
      getNoticiasUf({
        uf_noticia,
        local_context,
        page,
      }),
    );

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, [dispatch, uf_noticia, local_context, page, history, intl]);

  // -----------------------------
  // Atualizar URL (sem fetch)
  // -----------------------------
  const handlePageChange = useCallback(
    (newPage) => {
      const nextParams = new URLSearchParams(location.search);
      nextParams.set('page', newPage);

      history.push({
        pathname: location.pathname,
        search: nextParams.toString(),
      });
    },
    [history, location.pathname, location.search],
  );

  // -----------------------------
  // Render
  // -----------------------------
  return (
    <div className="ui container">
      <Helmet title={intl.formatMessage(messages.title)} />

      <h1 className="documentFirstHeading">
        {intl.formatMessage(messages.title)} {getNameStateByCode(uf_noticia)}
      </h1>

      <DateSocialShareBody
        data={{ hideDate: true }}
        properties={{ '@id': currentUrl }}
      />

      <div className="results-wrapper">
        {loading && (
          <div className="d-flex justify-content-center flex-column align-items-center pt-5 pb-6">
            <div
              className="br-loading small"
              role="progressbar"
              aria-label={intl.formatMessage(messages.loadingAria)}
            />
            <div className="mt-2">
              {intl.formatMessage(messages.loadingMessage)}
            </div>
          </div>
        )}

        {!loading && items?.length === 0 && (
          <div className="br-message info">
            <div className="content">
              {intl.formatMessage(messages.noResults)}
            </div>
          </div>
        )}

        {!loading && items?.length > 0 && (
          <>
            <SpecificTemplate items={items} />

            <GovBrPagination
              pageSizes={[]}
              total={total}
              currentPage={page}
              pageSize={30}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default SearchUf;
